import { useEffect, useMemo, useState } from "react";
import Decimal from "decimal.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { UserDeleteDialog } from "./UserDeleteDialog";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { formatLeaveDays } from "../leave/LeaveBalance";
import { UserDialog } from "./UserDialog";
import type { IsoDateTime, UserRole } from "../../api/types";

// 使用者管理(老闆專屬)。
//
// **這一頁最重要的不是新增帳號,是那三條擋住誤操作的護欄。**
//
// 這個系統沒有主機端的救援指令 —— 沒有 create-superuser,沒有 rescue shell。
// 老闆一旦把自己降成員工、或把自己停用,設定頁從此進不去,唯一的補救是
// 有人 ssh 進 VPS 直接改資料庫。所以後端擋是保證,這裡擋是**讓人不會走到那一步**:
// 按鈕直接 disabled,並用 title 說出為什麼,不讓使用者按下去才從 400 得知。

/** 帳號狀態。與後端 app/models.py 的 User.status 同一組字面值。 */
// `DELETED` 不會出現在這一頁 —— 後端的 `GET /api/users` 已經把它濾掉了。
// 留在型別裡是因為那是 `User.status` 真正的值域(第 24 輪多的)。
export type UserStatus = "ACTIVE" | "DISABLED" | "DELETED";

/** GET /api/users 的一列。**沒有 password_hash,而且永遠不會有。** */
export interface AdminUser {
  id: number;
  email: string;
  name: string;
  phone: string;
  role: UserRole;
  status: UserStatus;
  can_view_cost: boolean;
  /**
   * 年度特休天數,字串(例 `"14.0"`)。**天數一律不經過 number。**
   *
   * 這一欄是 `/api/leave/balance` 的 `entitled`,也就是特休額度的唯一來源。
   * 舊的回應沒有這一欄時視為 0。
   */
  annual_leave_days: string;
  /** 入職日 YYYY-MM-DD,還沒填是 null。老闆在這張清單上直接改(業主 2026-09-25)。 */
  hire_date?: string | null;
  created_at: IsoDateTime;
}

// 三條護欄的說法。與後端 app/routers/users.py 的同一組句子 ——
// 兩邊講得不一樣的話,使用者會以為自己遇到的是兩種不同的錯誤。
export const CANNOT_DEMOTE_SELF = "不能調降自己的職稱";
export const CANNOT_DISABLE_SELF = "不能停用自己的帳號";
export const NEED_ONE_OWNER = "系統至少要保留一位啟用中的老闆";

export interface GuardReasons {
  /** 不為 null 就代表「改角色」要 disabled,而且這句話就是 title。 */
  role: string | null;
  /** 不為 null 就代表「停用」要 disabled,而且這句話就是 title。 */
  status: string | null;
}

/**
 * 這一列的兩個危險動作各自能不能按,不能按的話原因是什麼。
 *
 * 刻意做成純函式並匯出:護欄是這一頁的重點,要能被直接驗證,
 * 而不是只能靠「render 出來再看按鈕的樣子」間接推測。
 *
 * **「動到自己」的訊息優先於「最後一位老闆」。** 兩條同時成立時(系統只剩
 * 自己一位老闆,而他想把自己降級),前者才是老闆真正需要看到的那一句。
 */
export function guardReasons(
  target: AdminUser,
  currentUserId: number | null,
  users: AdminUser[],
): GuardReasons {
  const isSelf = currentUserId !== null && target.id === currentUserId;
  const isActiveOwner = target.role === "OWNER" && target.status === "ACTIVE";
  const otherActiveOwners = users.filter(
    (u) => u.id !== target.id && u.role === "OWNER" && u.status === "ACTIVE",
  ).length;
  const isLastOwner = isActiveOwner && otherActiveOwners === 0;

  const role = isSelf && target.role === "OWNER" ? CANNOT_DEMOTE_SELF : isLastOwner ? NEED_ONE_OWNER : null;
  const status = isSelf && target.status === "ACTIVE" ? CANNOT_DISABLE_SELF : isLastOwner ? NEED_ONE_OWNER : null;
  return { role, status };
}

/**
 * 角色下拉的選項。**這一份是唯一的那一份**,新增帳號的對話框也用它
 * (`UserDialog.tsx`)。
 *
 * 用字與後端 `app/routers/users.py` 的 `_ROLE_TEXT` 對齊 —— 兩邊講得不一樣,
 * 通知裡寫「你的角色改為財務」而畫面上寫別的字,使用者會以為是兩件事。
 *
 * **每一個 `UserRole` 都要在這裡有一列。** 少一列不是「少一個選項」而已:
 * `Select` 直接把 options map 成 `<option>`,DOM 找不到相符的值會自己退回第一個
 * 選項 —— 第十二輪加了 `FINANCE` 卻沒補這裡,於是財務那一列的角色顯示的是
 * 「老闆」,而老闆在那一格隨手選一下就把財務真的升成老闆了。
 */
export const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: "OWNER", label: "老闆" },
  { value: "STAFF", label: "員工" },
  { value: "FINANCE", label: "財務" },
];

// 「角色」在 2026-09-25 統一改稱「職稱」(業主:「系統 "角色" 統一改為 職稱」)。
const HEADERS = ["姓名", "Email", "職稱", "狀態", "年度特休", "入職日", "建立日期", "動作"];

/** 年度特休天數的資料庫精度是 NUMERIC(4,1) —— 小數一位,也就是半天。 */
const ENTITLEMENT_DECIMALS = 1;

export const ENTITLEMENT_NOT_A_NUMBER = "年度特休請填數字";
export const ENTITLEMENT_NEGATIVE = "年度特休不能是負數";

export interface EntitlementParse {
  /** 可以送給後端的天數字串;不合法時是 null。 */
  value: string | null;
  error: string | null;
}

/**
 * 把欄位裡打的字轉成可以送出的年度特休天數。
 *
 * **全程 decimal.js,絕不 parseFloat / Number。** 這個數字會被拿去減掉已核准的
 * 特休算出剩餘天數,浮點誤差會變成「你還有 0.30000000000000004 天」。
 *
 * 留白視為 0(「這個人今年沒有特休」是一種合法的設定,不是漏填);看不懂的字
 * 與負數一律擋在畫面上 —— 負的額度一開始就是負的剩餘,看起來像已經超支。
 */
export function parseEntitlement(text: string): EntitlementParse {
  const trimmed = text.trim();
  if (trimmed === "") return { value: "0", error: null };
  let amount: Decimal;
  try {
    amount = new Decimal(trimmed);
  } catch {
    return { value: null, error: ENTITLEMENT_NOT_A_NUMBER };
  }
  if (!amount.isFinite()) return { value: null, error: ENTITLEMENT_NOT_A_NUMBER };
  if (amount.isNegative()) return { value: null, error: ENTITLEMENT_NEGATIVE };
  // 資料庫只存到小數一位,這裡先進位成同一個精度:送 13.55 上去會被默默存成
  // 13.6,畫面卻還顯示 13.55,同一個數字就有了兩種說法。
  return { value: amount.toDecimalPlaces(ENTITLEMENT_DECIMALS, Decimal.ROUND_HALF_UP).toString(), error: null };
}

/** 兩個天數字串是不是同一個數。`"14"` 與 `"14.0"` 是同一天,不該送出一次 PATCH。 */
function sameDays(left: string, right: string): boolean {
  const parsedLeft = parseEntitlement(left);
  const parsedRight = parseEntitlement(right);
  if (parsedLeft.value === null || parsedRight.value === null) return false;
  return new Decimal(parsedLeft.value).equals(new Decimal(parsedRight.value));
}

export interface EntitlementCellProps {
  user: AdminUser;
  /** 送出新的天數。只有真的改過、而且合法時才會被呼叫。 */
  onSave: (days: string) => void;
}

/**
 * 清單上「年度特休」那一格。**老闆唯一設得動特休額度的地方。**
 *
 * 改完離開欄位(或按 Enter)就送出,不另外放一顆儲存鈕:一列一顆鈕在一張
 * 十幾個人的清單上就是十幾顆,而這一格改的頻率是一年一次。
 */
export function EntitlementCell({ user, onSave }: EntitlementCellProps) {
  const shown = formatLeaveDays(user.annual_leave_days);
  const [text, setText] = useState(shown);
  const [error, setError] = useState<string | null>(null);

  // 後端回來的值換了(自己剛存好,或別人改過之後重新載入)就跟著換。
  // 使用者正在打的字不會被蓋掉 —— 這個 effect 只在來源值真的變了才跑。
  useEffect(() => {
    setText(shown);
    setError(null);
  }, [shown]);

  const commit = () => {
    const parsed = parseEntitlement(text);
    setError(parsed.error);
    if (parsed.value === null) return;
    if (sameDays(text, user.annual_leave_days)) return;
    onSave(parsed.value);
  };

  return (
    <Input
      aria-label={`${labelOf(user)} 的年度特休`}
      numeric
      inputMode="decimal"
      className="h-8 max-w-[6rem]"
      value={text}
      error={error}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export interface HireDateCellProps {
  user: AdminUser;
  /** 送出新的入職日;清空時是 null。只有真的改過才會被呼叫。 */
  onSave: (date: string | null) => void;
}

/**
 * 清單上「入職日」那一格(業主 2026-09-25:「個人資料追加 入職日」)。
 * **老闆唯一設得動入職日的地方**,本人在個人資料只能看 —— 與年度特休同一種所有權,
 * 也同一種做法:改完離開欄位就存,不另放儲存鈕。清空就是「還沒填」,送 null。
 */
export function HireDateCell({ user, onSave }: HireDateCellProps) {
  const saved = user.hire_date ?? "";
  const [text, setText] = useState(saved);

  useEffect(() => {
    setText(saved);
  }, [saved]);

  const commit = () => {
    if (text === saved) return;
    onSave(text === "" ? null : text);
  };

  return (
    <Input
      type="date"
      aria-label={`${labelOf(user)} 的入職日`}
      className="h-8 max-w-[10rem]"
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export interface NameCellProps {
  user: AdminUser;
  onSave: (name: string) => void;
}

/**
 * 就地改姓名(業主 2026-09-17:「老闆設定中 在每個使用者 都還要有一個
 * 修改名稱(修改姓名)」)。
 *
 * ----------------------------------------------------------------------
 * 為什麼是就地編輯,不另開對話框
 * ----------------------------------------------------------------------
 * 與同一張表上的年度特休同一個做法:點進去打字、離開就存。
 * 為了改一個字開一個對話框、按兩次確定,比錯字本身還煩。
 *
 * ----------------------------------------------------------------------
 * 空白擋下來
 * ----------------------------------------------------------------------
 * 姓名清空之後,畫面上到處都會退回顯示 Email(報價單的負責同仁、通知的
 * 發話者、請假單、業務績效表全部靠它)—— 那看起來像資料壞了。
 *
 * **輸入框裡放的是 `user.name` 本身,不是 `labelOf(user)`。** 沒填姓名的帳號
 * 那一格要是空的:塞 Email 進去的話,按一下 Enter 就把 Email 存成了他的姓名。
 */
export function NameCell({ user, onSave }: NameCellProps) {
  const [text, setText] = useState(user.name);
  const [error, setError] = useState<string | null>(null);

  // 後端回來的值換了就跟著換;使用者正在打的字不會被蓋掉。
  useEffect(() => {
    setText(user.name);
    setError(null);
  }, [user.name]);

  const commit = () => {
    const next = text.trim();
    if (next === "") {
      setError("姓名不能是空白");
      return;
    }
    setError(null);
    // 沒改就不送 —— 點進去又點出來不該算一次異動。
    if (next === user.name) return;
    onSave(next);
  };

  return (
    <Input
      aria-label={`${labelOf(user)} 的姓名`}
      className="h-8 max-w-[10rem]"
      value={text}
      error={error}
      errorRole="alert"
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}

/** 這個人在畫面上怎麼稱呼。沒填姓名就用 Email,總之不能是空白。 */
function labelOf(user: AdminUser): string {
  return user.name.trim() !== "" ? user.name : user.email;
}

/** 建立日期只顯示到日。時分對「這個帳號什麼時候開的」沒有意義。 */
function createdOn(user: AdminUser): string {
  return formatTaipeiDateTime(user.created_at).slice(0, 10);
}

export interface UserListProps {
  /** 目前登入者的 id。護欄靠它認出「自己那一列」。 */
  currentUserId: number | null;
}

export function UserList({ currentUserId }: UserListProps) {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  // 正在確認要刪掉的那一個。**兩段式** —— 只有已停用的列按得到。
  const [deleting, setDeleting] = useState<AdminUser | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<AdminUser[]>("/users"),
  });

  const users = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<AdminUser>(`/users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
    // 後端那三條護欄的原句照抄。前端不自己另編一句,否則同一件事會有兩種說法。
    onError: (err) => push(err instanceof Error ? err.message : "改不動這個帳號", "error"),
  });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">使用者</h3>
        <Button
          variant="primary"
          onClick={() => setCreating(true)}
          icon={<Plus aria-hidden="true" size={16} />}
        >
          新增帳號
        </Button>
      </div>
      <p className="m-0 text-sm text-[var(--text-2)]">
        同事離職先<strong>停用</strong>,回鍋時原地啟用即可,不必再開一組新帳號。
        確定不會回來了,停用的那一列上會出現<strong>刪除</strong> —— 他從此登不進來,
        但出勤、請假與他負責過的報價單全部留著。
      </p>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          使用者清單載入失敗,請稍後再試。
        </p>
      ) : null}

      {users.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="使用者清單" className="w-full min-w-[48rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {HEADERS.map((header) => (
                  <th key={header} scope="col" className="px-3 py-2">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const label = labelOf(user);
                const reasons = guardReasons(user, currentUserId, users);
                const active = user.status === "ACTIVE";

                return (
                  <tr key={user.id} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2">
                      {/* 姓名改得動(業主 2026-09-17)。**沒有護欄** ——
                          改自己的角色與狀態會有「把自己鎖在門外」的風險,
                          改名字沒有,擋住只是讓老闆得請別人幫忙改自己的名字。 */}
                      <NameCell
                        user={user}
                        onSave={(name) => patch.mutate({ id: user.id, body: { name } })}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-[var(--text-2)]">{user.email}</td>
                    <td className="px-3 py-2">
                      <Select
                        aria-label={`${label} 的職稱`}
                        title={reasons.role ?? undefined}
                        disabled={reasons.role !== null}
                        value={user.role}
                        options={ROLE_OPTIONS}
                        onChange={(event) => {
                          // disabled 的控制項理論上收不到事件,但護欄不靠「理論上」——
                          // 這裡再擋一次,程式上的誤用也送不出去。
                          if (reasons.role !== null) return;
                          patch.mutate({ id: user.id, body: { role: event.target.value } });
                        }}
                        className="h-8"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {/* 徽章一律「顏色 + 文字」,不可只靠顏色分辨狀態。 */}
                      <Badge tone={active ? "ok" : "neutral"}>{active ? "啟用" : "已停用"}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {/* 業主 2026-09-12 確認:「每人一個年度特休天數(老闆設定)」。
                          整支 /api/users 是老闆專屬,所以這一格只有老闆看得到。 */}
                      <EntitlementCell
                        user={user}
                        onSave={(days) => patch.mutate({ id: user.id, body: { annual_leave_days: days } })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <HireDateCell
                        user={user}
                        onSave={(date) => patch.mutate({ id: user.id, body: { hire_date: date } })}
                      />
                    </td>
                    <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{createdOn(user)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant={active ? "secondary" : "primary"}
                          aria-label={`${active ? "停用" : "啟用"} ${label}`}
                          title={reasons.status ?? undefined}
                          disabled={reasons.status !== null}
                          icon={
                            active ? (
                              <UserX aria-hidden="true" size={14} />
                            ) : (
                              <UserCheck aria-hidden="true" size={14} />
                            )
                          }
                          onClick={() => {
                            if (reasons.status !== null) return;
                            patch.mutate({
                              id: user.id,
                              body: { status: active ? "DISABLED" : "ACTIVE" },
                            });
                          }}
                        >
                          {active ? "停用" : "啟用"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`重設 ${label} 的密碼`}
                          icon={<KeyRound aria-hidden="true" size={14} />}
                          onClick={() => setResetting(user)}
                        >
                          重設密碼
                        </Button>
                        {/* **刪除只出現在已停用那一列**(業主 2026-09-21:
                            「可能這員工離職了 …… 還要刪除!」)。兩段式:
                            手滑刪不掉還在職的同事。 */}
                        {active ? null : (
                          <Button
                            size="sm"
                            variant="danger"
                            aria-label={`刪除 ${label}`}
                            icon={<Trash2 aria-hidden="true" size={14} />}
                            onClick={() => setDeleting(user)}
                          >
                            刪除
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {!isLoading && !error && users.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">還沒有任何帳號。</p>
      ) : null}

      <UserDialog
        mode="create"
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["users"] })}
      />
      {/* 代設密碼不會動到清單上的任何一欄,所以不必重新載入。 */}
      <UserDialog
        mode="reset"
        open={resetting !== null}
        user={resetting}
        onClose={() => setResetting(null)}
      />

      <UserDeleteDialog user={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
